import React from 'react';
import {
    Drawer,
    Box,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Typography,
    Button,
    Avatar,
} from '@mui/material';
import {
    Dashboard as DashboardIcon,
    Group as GroupIcon,
    Assignment as AssignmentIcon,
    EmojiEvents as EmojiEventsIcon,
    Business as BusinessIcon,
    Add as AddIcon,
    Settings as SettingsIcon,
    Logout as LogoutIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import UniversityLogo from '../pages/Nims_Logo.png';

const drawerWidth = 260;

/* ===============================
   THEME (Red–Blue Professional)
================================ */
const COLORS = {
    blue: '#0D47A1',
    red: '#D32F2F',
    bg: '#FFFFFF',
    light: '#F5F7FA',
    text: '#263238',
    muted: '#607D8B',
};

export default function Sidebar({ open, onToggle }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { userProfile, logout } = useAuth();

    /* ===============================
       MENU CONFIG
    ================================ */
    const menuItems = [
        {
            label: 'Dashboard',
            icon: <DashboardIcon />,
            path: userProfile?.role === 'admin' ? '/admin' : '/',
        },

        { label: 'Users', icon: <GroupIcon />, path: '/users', requiredRole: 'admin' },
        { label: 'All Startups', icon: <BusinessIcon />, path: '/startups', requiredRole: 'admin' },
        { label: 'Add Startup', icon: <AddIcon />, path: '/startups/new', requiredRole: 'admin' },
        { label: 'Competitions', icon: <EmojiEventsIcon />, path: '/competitions', requiredRole: 'admin' },
        { label: 'Form Management', icon: <AssignmentIcon />, path: '/form-builder', requiredRole: 'admin' },
        { label: 'Scoreboard', icon: <EmojiEventsIcon />, path: '/scoreboard', requiredRole: 'admin' },

        // Student routes (visible to students AND startups)
        { label: 'Profile', icon: <SettingsIcon />, path: '/profile', requiredRole: ['student', 'startup'] },

        // Startup-only pages (visible only to startups)
        { label: 'My Startup', icon: <BusinessIcon />, path: '/mystartup/profile', requiredRole: 'startup' },
        // { label: 'Startup Dashboard', icon: <DashboardIcon />, path: '/startup/dashboard', requiredRole: 'startup' },


        // { label: 'Profile', icon: <SettingsIcon />, path: '/profile' },
    ];

    const filteredMenu = menuItems.filter((item) => {
        // if no requiredRole => show to everyone
        if (!item.requiredRole) return true;

        // support array of roles or single role string
        if (Array.isArray(item.requiredRole)) {
            return item.requiredRole.includes(userProfile?.role);
        }
        return userProfile?.role === item.requiredRole;
    });

    const handleNavigate = (path) => {
        navigate(path);
        if (window.innerWidth < 960 && onToggle) onToggle();
    };

    const isActive = (path) => location.pathname === path;

    /* ===============================
       SIDEBAR CONTENT
    ================================ */
    const drawerContent = (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: COLORS.bg }}>

            {/* ===== HEADER ===== */}
            <Box
                sx={{
                    p: 2.5,
                    borderBottom: '1px solid #eee',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Box
                    component="img"
                    src={UniversityLogo}
                    alt="University Logo"
                    sx={{
                        maxWidth: '100%',
                        maxHeight: 48,
                        objectFit: 'contain',
                    }}
                />
            </Box>
            {/* <Box sx={{ p: 2.5, borderBottom: '1px solid #eee' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.blue }}>
                    {userProfile?.role === 'admin' ? 'Admin Panel' : 'Student Dashboard'}

                </Typography>
                <Typography variant="caption" sx={{ color: COLORS.muted }}>
                    {userProfile?.role === 'admin'
                        ? 'Incubation Portal'
                        : 'Student Portal'}

                </Typography>
            </Box> */}

            {/* ===== USER INFO ===== */}
            <Box sx={{ p: 2, textAlign: 'center', borderBottom: '1px solid #eee' }}>
                <Avatar
                    sx={{
                        mx: 'auto',
                        mb: 1,
                        bgcolor: COLORS.blue,
                        width: 48,
                        height: 48,
                    }}
                >
                    {userProfile?.firstName?.charAt(0)?.toUpperCase() || 'A'}
                </Avatar>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {userProfile?.firstName || 'Admin'}
                </Typography>
                <Typography variant="caption" sx={{ color: COLORS.muted }}>
                    {userProfile?.email}
                </Typography>
            </Box>

            {/* ===== MENU ===== */}
            <List sx={{ px: 1, py: 2 }}>
                {filteredMenu.map((item, index) => (
                    <React.Fragment key={item.path}>
                        {(item.label === 'All Startups') && <Divider sx={{ my: 1 }} />}

                        <ListItem
                            button
                            onClick={() => handleNavigate(item.path)}
                            selected={isActive(item.path)}
                            sx={{
                                mb: 0.5,
                                borderRadius: 1,
                                '&.Mui-selected': {
                                    bgcolor: '#E3F2FD',
                                    borderLeft: `4px solid ${COLORS.red}`,
                                },
                                '&:hover': {
                                    bgcolor: COLORS.light,
                                },
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 40,
                                    color: isActive(item.path) ? COLORS.red : COLORS.muted,
                                }}
                            >
                                {item.icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.label}
                                primaryTypographyProps={{
                                    fontSize: '0.95rem',
                                    fontWeight: isActive(item.path) ? 600 : 500,
                                    color: isActive(item.path) ? COLORS.blue : COLORS.text,
                                }}
                            />
                        </ListItem>
                    </React.Fragment>
                ))}
            </List>

            <Box sx={{ flexGrow: 1 }} />

            {/* ===== LOGOUT ===== */}
            <Box sx={{ p: 2, borderTop: '1px solid #eee' }}>
                <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<LogoutIcon />}
                    onClick={logout}
                    sx={{
                        borderColor: COLORS.red,
                        color: COLORS.red,
                        '&:hover': {
                            bgcolor: '#FDECEA',
                        },
                    }}
                >
                    Logout
                </Button>
            </Box>

            {/* ===== FOOTER ===== */}
            <Box sx={{ p: 2 }}>
                <Typography
                    variant="caption"
                    sx={{ display: 'block', textAlign: 'center', color: COLORS.muted }}
                >
                    © 2025 Incubation Portal
                </Typography>
            </Box>
        </Box>
    );

    /* ===============================
       DRAWERS
    ================================ */
    return (
        <>
            {/* Mobile */}
            <Drawer
                variant="temporary"
                open={open}
                onClose={onToggle}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': { width: drawerWidth },
                }}
            >
                {drawerContent}
            </Drawer>

            {/* Desktop */}
            <Box
                sx={{
                    display: { xs: 'none', md: 'block' },
                    width: drawerWidth,
                    height: '100vh',
                    position: 'sticky',
                    top: 0,
                    borderRight: '1px solid #eee',
                }}
            >
                {drawerContent}
            </Box>
        </>
    );
}
